import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../../css/signup5.css";
import NotificationSwitch from "../../Components/SettingComponents/NotificationItem";
import { useNavigate } from "react-router-dom";
import {
  useGetNotificationSettings,
  useUpdateNotificationSettings,
} from "../../Controllers/SettingsController";
import { authLogger } from "../../Utils/logger";
import ContainedButton from "../../Components/Buttons/ContainedButton";

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notificationStates, setNotificationStates] = useState({
    all: true,
    checkInReminder: true,
    newMessage: true,
    newVideo: true,
    newReply: true,
  });
  const [originalStates, setOriginalStates] = useState({
    all: true,
    checkInReminder: true,
    newMessage: true,
    newVideo: true,
    newReply: true,
  });
  const [error, setError] = useState(null);

  // Use the new controller hooks
  const {
    data: notificationData,
    isLoading,
    error: fetchError,
  } = useGetNotificationSettings();
  const updateMutation = useUpdateNotificationSettings();

  // Memoize changes detection
  const hasChanges = useMemo(() => {
    return (
      notificationStates.checkInReminder !== originalStates.checkInReminder ||
      notificationStates.newMessage !== originalStates.newMessage ||
      notificationStates.newVideo !== originalStates.newVideo ||
      notificationStates.newReply !== originalStates.newReply
    );
  }, [notificationStates, originalStates]);

  // Handle notification data from API
  useEffect(() => {
    if (notificationData) {
      try {
        // Set individual toggles from API response
        const updatedStates = {
          checkInReminder:
            notificationData["Check In Reminder"]?.check_in_reminder ?? true,
          newMessage:
            notificationData["New Message Added"]?.new_message_added ?? true,
          newVideo:
            notificationData["New Video Added"]?.new_video_added ?? true,
          newReply:
            notificationData["New Reply To Message"]?.new_reply_to_message ??
            true,
        };

        // Set the "all" toggle based on individual toggles
        updatedStates.all =
          updatedStates.checkInReminder &&
          updatedStates.newMessage &&
          updatedStates.newVideo &&
          updatedStates.newReply;

        setNotificationStates(updatedStates);
        setOriginalStates({ ...updatedStates });
        authLogger.info("Notification settings loaded successfully");
      } catch (error) {
        authLogger.error("Error processing notification settings", error);
        setError(
          "Failed to load notification settings. Please try again later.",
        );
      }
    }
  }, [notificationData]);

  // Handle fetch errors
  useEffect(() => {
    if (fetchError) {
      authLogger.error("Failed to fetch notification settings", fetchError);
      setError("Failed to load notification settings. Please try again later.");
    }
  }, [fetchError]);

  // Toggle notification local state only (no API call) - memoized
  const toggleNotification = useCallback((key) => {
    setNotificationStates((prevStates) => {
      const updatedStates = { ...prevStates };

      // Handle special case for "all" toggle
      if (key === "all") {
        const newAllState = !updatedStates.all;

        // Set all individual toggles to match the "all" toggle state
        updatedStates.all = newAllState;
        updatedStates.checkInReminder = newAllState;
        updatedStates.newMessage = newAllState;
        updatedStates.newVideo = newAllState;
        updatedStates.newReply = newAllState;
      } else {
        // Toggle the specific individual setting
        updatedStates[key] = !updatedStates[key];

        // Set "all" toggle based on individual toggles
        updatedStates.all =
          updatedStates.checkInReminder &&
          updatedStates.newMessage &&
          updatedStates.newVideo &&
          updatedStates.newReply;
      }

      return updatedStates;
    });
  }, []);

  const submitNotificationChanges = useCallback(async () => {
    setError(null);

    const payload = {
      check_in_reminder: notificationStates.checkInReminder,
      new_message_added: notificationStates.newMessage,
      new_video_added: notificationStates.newVideo,
      new_reply_to_message: notificationStates.newReply,
    };

    try {
      await updateMutation.mutateAsync(payload);
      setOriginalStates({ ...notificationStates });
      authLogger.info("Notification settings updated successfully");
      navigate("/get-started");
    } catch (error) {
      authLogger.error("Error updating notification settings", error);
      setError("Failed to update notification settings. Please try again.");
    }
  }, [notificationStates, updateMutation, navigate]);

  const handleDoneClick = useCallback(() => {
    if (hasChanges) {
      submitNotificationChanges();
    } else {
      navigate("/get-started");
    }
  }, [hasChanges, submitNotificationChanges, navigate]);

  return (
    <div
      className="onboarding-container"
      style={{ color: "rgba(230, 236, 241, 1)" }}
    >
      <div className="onboard-content">
        <div className="d-flex flex-column justify-content-center align-items-center notification">
          <h1
            style={{ marginTop: "56px", marginBottom: "40px" }}
            className="text-heading-h3"
          >
            Push Notification
          </h1>

          {/* {error && (
            <div className="alert alert-danger mb-4" role="alert">
              {error}
            </div>
          )} */}

          <div className="d-flex flex-column align-items-center w-100 padding-card">
            <div className="notifications-container gap-3">
              <NotificationSwitch
                label="All"
                checked={notificationStates.all}
                onToggle={() => toggleNotification("all")}
                disabled={isLoading}
              />
              <hr
                style={{
                  border: "1px solid rgba(49, 60, 69, 1)",
                  margin: 0,
                  opacity: 1,
                }}
              />
              <NotificationSwitch
                label="Check-in reminder"
                checked={notificationStates.checkInReminder}
                onToggle={() => toggleNotification("checkInReminder")}
                disabled={isLoading}
              />
              <NotificationSwitch
                label="New message added"
                checked={notificationStates.newMessage}
                onToggle={() => toggleNotification("newMessage")}
                disabled={isLoading}
              />
              <NotificationSwitch
                label="New video added"
                checked={notificationStates.newVideo}
                onToggle={() => toggleNotification("newVideo")}
                disabled={isLoading}
              />
              <NotificationSwitch
                label="New reply to message"
                checked={notificationStates.newReply}
                onToggle={() => toggleNotification("newReply")}
                disabled={isLoading}
              />
            </div>{" "}
            {error && (
              <p style={{ color: "red", marginTop: "16px" }}>{error}</p>
            )}
            <div
              className="d-flex flex-column flex-sm-row gap-3 gap-sm-4 terms-buttons justify-content-between align-items-center w-100"
              style={{ maxWidth: 345, marginTop: "30px", marginBottom: "40px" }}
            >
              <ContainedButton
                style={{ maxWidth: 160, width: "100%" }}
                className={`btn-1 btn-1-basic`}
                onClick={() => {
                  localStorage.clear();
                  navigate("/register");
                }}
                disabled={isLoading || updateMutation.isPending}
              >
                Back
              </ContainedButton>

              <ContainedButton
                type="submit"
                isLoading={updateMutation.isPending}
                disabled={isLoading || updateMutation.isPending}
                onClick={handleDoneClick}
                style={{
                  backgroundColor: updateMutation.isPending
                    ? "rgba(153, 209, 255, 1)"
                    : "rgba(0, 140, 255, 1)",
                  color: "rgba(230, 236, 241, 1)",
                  border: "1.5px solid rgba(0, 140, 255, 1)",
                  borderRadius: "24px",
                  padding: "6px 32px",
                  width: "100%",
                  height: "52px",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "24px",
                  cursor:
                    isLoading || updateMutation.isPending
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Done
              </ContainedButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
